import React, { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, MessageCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs.config';
import { content } from '../data/content';

const Contact = () => {
  const [formData, setFormData] = useState({
    nome: '',
    azienda: '',
    ruolo: '',
    email: '',
    telefono: '',
    tipoRichiesta: '',
    dataEvento: '',
    citta: '',
    budgetRange: '',
    messaggio: '',
    honeypot: '' // Spam protection field
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot spam protection - if filled, it's a bot
    if (formData.honeypot) {
      console.log('Spam detected');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        {
          nome: formData.nome,
          azienda: formData.azienda,
          ruolo: formData.ruolo,
          email: formData.email,
          telefono: formData.telefono,
          tipoRichiesta: formData.tipoRichiesta,
          dataEvento: formData.dataEvento || 'Non specificata',
          citta: formData.citta || 'Non specificata',
          budgetRange: formData.budgetRange,
          messaggio: formData.messaggio
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      if (result.status === 200) {
        setSubmitMessage({
          type: 'success',
          text: 'Grazie! La tua richiesta è stata inviata con successo. Ti risponderemo entro 24 ore.'
        });
        
        // Reset form
        setFormData({
          nome: '',
          azienda: '',
          ruolo: '',
          email: '',
          telefono: '',
          tipoRichiesta: '',
          dataEvento: '',
          citta: '',
          budgetRange: '',
          messaggio: '',
          honeypot: ''
        });
      }
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitMessage({
        type: 'error',
        text: 'Si è verificato un errore. Riprova o contattaci direttamente via email o telefono.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappUrl = `https://wa.me/${content.contact.phone.replace(/\s/g, '')}`;

  return (
    <section id="contact" className="py-24 bg-neutral-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-amber-50 mb-6">
            Contatti
          </h2>
          <p className="text-xl text-amber-100/70 max-w-3xl mx-auto">
            Parliamo della tua prossima attivazione di brand
          </p>
          <div className="w-24 h-1 bg-red-600 mx-auto mt-6"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-amber-50 mb-6">Informazioni di contatto</h3>
              
              <div className="space-y-6">
                {/* Name */}
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/10 rounded-lg p-3">
                    <Phone className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-amber-100/70 text-sm mb-1">Telefono</p>
                    <a href={`tel:${content.contact.phone}`} className="text-amber-50 font-semibold text-lg hover:text-amber-200 transition-colors">
                      {content.contact.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/10 rounded-lg p-3">
                    <Mail className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-amber-100/70 text-sm mb-1">Email</p>
                    <a href={`mailto:${content.contact.email}`} className="text-amber-50 font-semibold text-lg hover:text-amber-200 transition-colors">
                      {content.contact.email}
                    </a>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/10 rounded-lg p-3">
                    <MapPin className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-amber-100/70 text-sm mb-1">Sede</p>
                    <p className="text-amber-50 font-semibold text-lg">{content.contact.location}</p>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start space-x-4">
                  <div className="bg-red-600/10 rounded-lg p-3">
                    <Instagram className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-amber-100/70 text-sm mb-1">Instagram</p>
                    <a 
                      href={`https://instagram.com/${content.contact.instagram.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-50 font-semibold text-lg hover:text-amber-200 transition-colors"
                    >
                      {content.contact.instagram}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Contact Buttons */}
            <div className="space-y-4 pt-6">
              <a
                href={`tel:${content.contact.phone}`}
                className="flex items-center justify-center space-x-3 bg-red-600 hover:bg-red-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-red-600/50"
              >
                <Phone className="w-5 h-5" />
                <span>Chiama ora</span>
              </a>
              
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 bg-green-600 hover:bg-green-700 text-white px-6 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-green-600/50"
              >
                <MessageCircle className="w-5 h-5" />
                <span>WhatsApp</span>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-br from-amber-50/5 to-transparent border border-amber-200/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-amber-50 mb-2">Parliamo della tua prossima attivazione di brand</h3>
            <p className="text-amber-100/60 text-sm mb-8 font-light">Compila il form e ricevi una proposta personalizzata entro 24 ore</p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot field - hidden from users, catches bots */}
              <input
                type="text"
                name="honeypot"
                value={formData.honeypot}
                onChange={handleChange}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-amber-100/80 text-sm font-medium mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-amber-100/80 text-sm font-medium mb-2">
                    Azienda *
                  </label>
                  <input
                    type="text"
                    name="azienda"
                    value={formData.azienda}
                    onChange={handleChange}
                    required
                    className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-amber-100/80 text-sm font-medium mb-2">
                  Ruolo
                </label>
                <input
                  type="text"
                  name="ruolo"
                  value={formData.ruolo}
                  onChange={handleChange}
                  className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                  placeholder="es. Marketing Manager"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-amber-100/80 text-sm font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-amber-100/80 text-sm font-medium mb-2">
                    Telefono *
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-amber-100/80 text-sm font-medium mb-2">
                  Tipo di richiesta *
                </label>
                <select
                  name="tipoRichiesta"
                  value={formData.tipoRichiesta}
                  onChange={handleChange}
                  required
                  className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                >
                  <option value="">Seleziona...</option>
                  <option value="brand-activation">Brand Activation</option>
                  <option value="roadshow">Roadshow</option>
                  <option value="corporate-event">Corporate Event</option>
                  <option value="shooting">Shooting</option>
                  <option value="food-event">Food Event</option>
                  <option value="altro">Altro</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-amber-100/80 text-sm font-medium mb-2">
                    Data evento (indicativa)
                  </label>
                  <input
                    type="date"
                    name="dataEvento"
                    value={formData.dataEvento}
                    onChange={handleChange}
                    className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-amber-100/80 text-sm font-medium mb-2">
                    Città
                  </label>
                  <input
                    type="text"
                    name="citta"
                    value={formData.citta}
                    onChange={handleChange}
                    className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-amber-100/80 text-sm font-medium mb-2">
                  Budget range *
                </label>
                <select
                  name="budgetRange"
                  value={formData.budgetRange}
                  onChange={handleChange}
                  required
                  className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all"
                >
                  <option value="">Seleziona...</option>
                  <option value="< 2.000€">{"< 2.000€"}</option>
                  <option value="2.000€ - 5.000€">2.000€ - 5.000€</option>
                  <option value="5.000€ - 10.000€">5.000€ - 10.000€</option>
                  <option value="> 10.000€">{"> 10.000€"}</option>
                  <option value="da definire">Da definire</option>
                </select>
              </div>

              <div>
                <label className="block text-amber-100/80 text-sm font-medium mb-2">
                  Messaggio *
                </label>
                <textarea
                  name="messaggio"
                  value={formData.messaggio}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full bg-neutral-900/50 border border-amber-200/20 rounded-lg px-4 py-3 text-amber-50 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none transition-all resize-none"
                  placeholder="Raccontaci del tuo progetto..."
                ></textarea>
              </div>

              {submitMessage.text && (
                <div className={`${
                  submitMessage.type === 'success' 
                    ? 'bg-green-600/20 border-green-600/30 text-green-400' 
                    : 'bg-red-600/20 border-red-600/30 text-red-400'
                } border rounded-lg p-4`}>
                  <p className="text-center text-sm">{submitMessage.text}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white px-6 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-red-600/50"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia richiesta'}
              </button>

              {/* EmailJS Configuration Notice */}
              {EMAILJS_CONFIG.PUBLIC_KEY === 'YOUR_PUBLIC_KEY_HERE' && (
                <div className="bg-amber-600/20 border border-amber-600/30 rounded-lg p-4 mt-4">
                  <p className="text-amber-400 text-xs text-center">
                    ⚠️ EmailJS non ancora configurato. Segui le istruzioni in /src/config/emailjs.config.js
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
